import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Stack,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import * as React from 'react';
import { useParams } from 'react-router';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { Api } from '../../api';
import { format as dfFormat, parseISO, isValid } from 'date-fns';
import { serverImageUrl } from '../../util';
import { MEALS, getMealLabel, formatServingSize } from '../../models/Food';

function toISO(d: Date) {
  return dfFormat(d, 'yyyy-MM-dd');
}

function enumerateDates(startISO: string, endISO: string) {
  const outDates: string[] = [];
  const outLabels: string[] = [];
  const start = new Date(startISO);
  const end = new Date(endISO);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    outDates.push(toISO(d));
    outLabels.push(dfFormat(d, 'd MMM'));
  }
  return { isoDates: outDates, labels: outLabels };
}

export default function UserFoodAndWater() {
  const { id = '' } = useParams();
  const { getParams } = useModifiableParams({
    defaults: { timeframe: 'week' },
  });
  const params = getParams();

  const [modalPhoto, setModalPhoto] = React.useState<{
    name: string;
    imageUrl: string;
  } | null>(null);

  let startDate: string | undefined = params.startDate;
  let endDate: string | undefined = params.endDate;
  if (!startDate || !endDate) {
    const today = new Date();
    if (params.timeframe === 'month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate = toISO(first);
      endDate = toISO(today);
    } else {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      startDate = toISO(start);
      endDate = toISO(today);
    }
  }

  const { isoDates, labels } = enumerateDates(startDate!, endDate!);

  const macrosApi =
    Api.MetricMeal.useReadCollectionMacronutrientsStartDateEndDate(
      id,
      startDate!,
      endDate!,
    );
  const mealsApi = Api.MetricMeal.useReadCollectionMetricMealsStartDateEndDate(
    id,
    startDate!,
    endDate!,
  );

  const macronutrientData = {
    carbs: isoDates.map(() => 0),
    protein: isoDates.map(() => 0),
    fat: isoDates.map(() => 0),
  } as { carbs: number[]; protein: number[]; fat: number[] };
  for (const m of macrosApi.data?.member || []) {
    const idx = isoDates.indexOf(m.date);
    if (idx >= 0) {
      macronutrientData.carbs[idx] = m.carbs ?? 0;
      macronutrientData.protein[idx] = m.protein ?? 0;
      macronutrientData.fat[idx] = m.fat ?? 0;
    }
  }

  const waterIntakeData = isoDates.map((d) => {
    const day = (mealsApi.data?.member || []).find((x) => x.date === d);
    const amountCl = day?.waterIntake?.amount ?? 0;
    return Number(amountCl) / 100;
  });

  const avg = (arr: number[]) => {
    const nums = arr.filter((n) => n > 0);
    if (!nums.length) return 0;
    return (
      Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 100) / 100
    );
  };

  const avgCarbs = avg(macronutrientData.carbs);
  const avgProtein = avg(macronutrientData.protein);
  const avgFat = avg(macronutrientData.fat);
  const avgWater = avg(waterIntakeData);

  const foodLogs = (mealsApi.data?.member || [])
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .map((day) => {
      const d = parseISO(day.date);
      const dateLabel = isValid(d) ? dfFormat(d, 'EEE, d MMM yyyy') : day.date;

      const byKey = new Map<string, (typeof day.meals)[number]>();
      for (const m of day.meals || []) {
        if (m?.meal) byKey.set(m.meal, m);
      }

      const orderedMeals = Object.keys(MEALS).map((key) => {
        const m = byKey.get(key);
        const type = getMealLabel(key);
        const hf =
          m?.hungerFullnessLevel != null
            ? `HF: ${m.hungerFullnessLevel}`
            : 'HF: -';

        const processedItems: Array<{
          name?: string;
          description?: string;
          imageId?: string;
        }> = [];

        if (m) {
          for (const it of m.items) {
            const name = it.foodItem ? it.foodItem.name : it.customMealName;
            const unit = it.foodItem?.unit;
            const qty = it.serving != null ? String(it.serving) : undefined;

            let formattedName: string | undefined;
            if (name) {
              if (qty && unit) {
                formattedName = `${formatServingSize(qty, unit)} ${name}`;
              } else {
                formattedName = name;
              }
            }

            const itemDescription = it.description || undefined;
            const imageId = it.image?.id;

            if (formattedName || itemDescription || imageId) {
              processedItems.push({
                name: formattedName,
                description: itemDescription,
                imageId,
              });
            }
          }
        }

        return {
          type,
          hf,
          items: processedItems,
        };
      });

      return { date: dateLabel, meals: orderedMeals };
    });

  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Card>
            <CardHeader
              sx={{ alignItems: 'flex-start' }}
              title={
                <Typography variant="h6">
                  Macronutrients (rough estimate)
                </Typography>
              }
              action={
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle2">Average/day</Typography>
                    <Stack direction="row" spacing={3}>
                      <Stack alignItems="flex-start">
                        <Typography variant="subtitle1">{avgCarbs}g</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Carbs
                        </Typography>
                      </Stack>
                      <Stack alignItems="flex-start">
                        <Typography variant="subtitle1">
                          {avgProtein}g
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Protein
                        </Typography>
                      </Stack>
                      <Stack alignItems="flex-start">
                        <Typography variant="subtitle1">{avgFat}g</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fat
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    In grams
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#2A4F65',
                        }}
                      />
                      <Typography variant="caption">Carbs</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#22CBEE',
                        }}
                      />
                      <Typography variant="caption">Protein</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: '#15B79F',
                        }}
                      />
                      <Typography variant="caption">Fat</Typography>
                    </Box>
                  </Stack>
                </Stack>

                <Box sx={{ width: '100%', height: 400 }}>
                  <LineChart
                    series={[
                      {
                        data: macronutrientData.carbs,
                        label: 'Carbs',
                        color: '#2A4F65',
                        curve: 'monotoneX',
                      },
                      {
                        data: macronutrientData.protein,
                        label: 'Protein',
                        color: '#22CBEE',
                        curve: 'monotoneX',
                      },
                      {
                        data: macronutrientData.fat,
                        label: 'Fat',
                        color: '#15B79F',
                        curve: 'monotoneX',
                      },
                    ]}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: labels,
                        disableLine: true,
                        disableTicks: true,
                      },
                    ]}
                    yAxis={[
                      {
                        disableLine: true,
                        disableTicks: true,
                      },
                    ]}
                    grid={{ horizontal: true, vertical: false }}
                    margin={{ left: 50, right: 30, top: 20, bottom: 50 }}
                    slotProps={{
                      line: {
                        style: { strokeWidth: 2 },
                      },
                      mark: { style: { display: 'none' } },
                    }}
                    hideLegend
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6}>
          <Card>
            <CardHeader
              sx={{ alignItems: 'flex-start' }}
              title={<Typography variant="h6">Water Intake</Typography>}
              action={
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle2">Average/day</Typography>
                    <Typography variant="subtitle1">{avgWater}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      litres
                    </Typography>
                  </Stack>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ width: '100%', height: 400 }}>
                <BarChart
                  series={[
                    {
                      data: waterIntakeData,
                      color: '#5A7280',
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: labels,
                      disableLine: true,
                      disableTicks: true,
                      categoryGapRatio: 0.6,
                    },
                  ]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      valueFormatter: (value: number) =>
                        value > 0 ? `${value}L` : `${value}`,
                      min: 0,
                    },
                  ]}
                  grid={{ horizontal: true, vertical: false }}
                  slotProps={{
                    bar: {
                      style: {
                        width: 32,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardHeader
          title={<Typography variant="h6">Food Logs</Typography>}
          action={
            <Typography variant="body2" color="text.secondary">
              HF = Hunger-Fullness Scale
            </Typography>
          }
        />
        <CardContent>
          <Stack spacing={2}>
            {foodLogs.map((day, dayIndex) => (
              <Box
                key={dayIndex}
                sx={{
                  bgcolor: 'var(--mui-palette-background-level1)',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {day.date}
                </Typography>

                <Stack
                  direction="row"
                  divider={<Divider orientation="vertical" flexItem />}
                  spacing={0}
                >
                  {day.meals.map((meal, mealIndex) => (
                    <Box
                      key={mealIndex}
                      sx={{
                        flex: 1,
                        px: 2,
                        py: 1,
                        minHeight: 120,
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="subtitle2">
                            {meal.type}
                          </Typography>

                          <Chip
                            label={meal.hf}
                            size="small"
                            sx={{
                              fontSize: '0.75rem',
                              bgcolor: 'grey.200',
                              color: 'text.primary',
                            }}
                          />
                        </Stack>

                        {meal.items?.length ? (
                          <Stack spacing={1.5} sx={{ mt: 1 }}>
                            {meal.items.map((item, index) => (
                              <Box key={index}>
                                {item.imageId && (
                                  <Box
                                    component="img"
                                    src={serverImageUrl({
                                      id: item.imageId,
                                      w: 240,
                                    })}
                                    onClick={() =>
                                      setModalPhoto({
                                        name: item.name ?? '',
                                        imageUrl: serverImageUrl({
                                          id: item.imageId ?? '',
                                          w: 800,
                                        }),
                                      })
                                    }
                                    sx={{
                                      width: 120,
                                      height: 120,
                                      objectFit: 'cover',
                                      borderRadius: 1.25,
                                      mb: 0.5,
                                      cursor: 'pointer',
                                    }}
                                  />
                                )}
                                {(item.name || item.description) && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: '0.875rem',
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    •{' '}
                                    {item.name && item.description
                                      ? `${item.name} - ${item.description}`
                                      : item.name || item.description}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {modalPhoto && (
        <Dialog
          open={true}
          onClose={() => setModalPhoto(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography typography="h6">{modalPhoto.name}</Typography>
          </DialogTitle>

          <DialogContent>
            <Box
              component="img"
              src={modalPhoto.imageUrl}
              sx={{
                width: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1.25,
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Stack>
  );
}
